import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { getCollectionSuggests } from '../../data-layer/collections/service';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { toCollectionPersistenceUI } from '../../mappers/collections/toCollectionPersistenceUI';
import { PromoteShowInFeedDrawer } from '../../ui/users/PromoteShowInFeedDrawer/PromoteShowInFeedDrawer';
import { useEventSource } from '../../utils/LocationContext';
import { EventSource } from '../../utils/analyticsService';
import { useBackButton } from '../../utils/useBackButton';
import { useUpdateCollectionInfo } from './EditCollectionDialog/hooks/useUpdateCollectionInfo';
import { useCollection } from './useCollection';

export enum Step {
    CONFIRM = 'CONFIRM',
    CHOOSE_NAME = 'CHOOSE_NAME',
}

export const PromoteShowInFeedDialog = () => {
    const onNavigateBack = useBackButton();
    const { state } = useLocation();
    const dispatch = useDispatch();
    const [step, setStep] = useState<Step>(Step.CONFIRM);
    const { eventSource } = useEventSource();

    const { collectionModel } = useCollection({ collectionId: state?.collectionId });

    const initialValues = collectionModel ? toCollectionPersistenceUI(collectionModel) : undefined;

    const [suggests, setSuggests] = useState<string[]>([]);
    const [suggestsLoading, setSuggestsLoading] = useState(false);
    const [currentSuggest, setCurrentSuggest] = useState('');

    const loadSuggests = useCallback(async () => {
        if (!state?.collectionId) {
            return;
        }
        let suggestNames;

        try {
            setSuggestsLoading(true);

            suggestNames = await getCollectionSuggests(
                state.collectionId,
                eventSource ?? EventSource.POST,
            );

            if (!suggestNames?.length) {
                throw new Error('Нет саджестов');
            }
        } catch (error) {
            setStep(Step.CONFIRM);
            dispatch(showNotificationAsync('Не удалось сгенерировать названия. Попробуйте позже'));
            setSuggestsLoading(false);

            return;
        }
        setSuggests(suggestNames);
        setCurrentSuggest(suggestNames[0]);

        setSuggestsLoading(false);
    }, [dispatch, eventSource, state.collectionId]);

    useEffectOnce(() => {
        if (state?.step === Step.CHOOSE_NAME) {
            onChooseNameClick();
        }
    });

    const onChooseNameClick = useCallback(() => {
        setStep(Step.CHOOSE_NAME);
        loadSuggests();
    }, [loadSuggests]);

    const onChooseSuggest = useCallback((suggest: string) => {
        setCurrentSuggest(suggest);
    }, []);

    const onSubmit = useUpdateCollectionInfo({
        initialValues,
        newValues: {
            showInFeed: true,
            isPublic: true,
            collectionName: initialValues?.displayName ?? '',
            currentSuggest,
        },
    });

    const values = { suggests, currentSuggest, suggestsLoading };
    const handlers = { onChooseSuggest, onChooseNameClick, onSubmit };

    return (
        <PromoteShowInFeedDrawer
            step={step}
            onClose={onNavigateBack}
            values={values}
            handlers={handlers}
        />
    );
};
